import { ObjectFactory } from '@/config/objectFactory';
import { Organization, OrgLocation } from '@/types/entity';
import { AddLocationRequest, ApiRequest, UpdateLocationRequest } from '@/types/request';
import { ApiResponse, OrganizationRequestsCountResponse, Provider } from '@/types/response';
import { CommonUtils } from '@/util/commonUtils';
import { MedTransGoConstants } from '@/util/constants';
import { IOrganizationService } from '../iOrganizationService';

export class OrganizationService implements IOrganizationService {
    public async getLocations(baseURL: string): Promise<ApiResponse<OrgLocation[]>> {
        const memberProfile = ObjectFactory.getCacheService().getMemberInfo();
        const iHttpService = ObjectFactory.getHttpService();
        const organizationId = memberProfile.organization ? memberProfile.organization.id : '';
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_ORGANIZATIONS}/
                    ${organizationId}/locations`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.get<OrgLocation[]>(apiRequest);
        return response;
    }

    public async getLocationDetails(baseURL: string, locationId: string): Promise<ApiResponse<OrgLocation>> {
        const memberProfile = ObjectFactory.getCacheService().getMemberInfo();
        const iHttpService = ObjectFactory.getHttpService();
        const organizationId = memberProfile.organization ? memberProfile.organization.id : '';
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_ORGANIZATIONS}/${organizationId}/locations/${locationId}`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.get<OrgLocation>(apiRequest);
        return response;
    }

    public async addLocation(baseURL: string, addLocationRequest: AddLocationRequest):
        Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const member = ObjectFactory.getCacheService().getMemberInfo();
        const organizationId = member.organization ? member.organization.id : '';
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_ORGANIZATIONS}/${organizationId}/locations`,
            data: addLocationRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.post<AddLocationRequest>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async updateLocation(baseURL: string, locationId: string, updateLocationRequest: UpdateLocationRequest):
        Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const member = ObjectFactory.getCacheService().getMemberInfo();
        const organizationId = member.organization ? member.organization.id : '';
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_ORGANIZATIONS}/${organizationId}/locations/${locationId}`,
            data: updateLocationRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.put<UpdateLocationRequest>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async getOrgDetailsById(baseURL: string): Promise<ApiResponse<Organization>> {
        const memberProfile = ObjectFactory.getCacheService().getMemberInfo();
        const iHttpService = ObjectFactory.getHttpService();
        const organizationId = memberProfile.organization ? memberProfile.organization.id : '';
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_ORGANIZATIONS}/${organizationId}`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.get<Organization>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async updateOrganization(baseURL: string, organizationId: string, updateOrgRequest: any):
        Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_ORGANIZATIONS}/${organizationId}/update`,
            data: updateOrgRequest,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.put<Organization>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }
    public async enableApiIntegration(baseURL: string):
        Promise<ApiResponse<any>> {
        const memberProfile = ObjectFactory.getCacheService().getMemberInfo();
        const organizationId = memberProfile.organization ? memberProfile.organization.id : '';
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_ORGANIZATIONS}/${organizationId}/enableApi`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.put<Organization>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async verifyAccessCode(baseURL: string, accessCode: string): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_ORGANIZATIONS}/verify?accessCode=${accessCode}`,
        };
        const response = await iHttpService.get<any>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async uploadlocations(baseURL: string, excelFormData: FormData): Promise<ApiResponse<any>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_ORGANIZATIONS}/locations/upload`,
            data: excelFormData,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.post<any>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async getPreferredProviders(baseURL: string, language?: string):
        Promise<ApiResponse<Provider[]>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_ORGANIZATIONS}/requests/providers`,
            loaderEvent: {
                show: true,
            },
            params: {
                language,
            },
        };
        const response = await iHttpService.get<Provider[]>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }

    public async getOrganizationRequestCount(baseURL: string):
        Promise<ApiResponse<OrganizationRequestsCountResponse>> {
        const iHttpService = ObjectFactory.getHttpService();
        const apiRequest: ApiRequest = {
            baseURL,
            url: `${MedTransGoConstants.HTTP_URL_ORGANIZATIONS}/requests/count`,
            loaderEvent: {
                show: true,
            },
        };
        const response = await iHttpService.get<OrganizationRequestsCountResponse>(apiRequest);
        if (!response.success) {
            CommonUtils.showError(response);
        }
        return response;
    }


}
